import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { FaWhatsapp, FaChevronDown, FaChevronRight } from 'react-icons/fa';
import { companyInfo, navigationMenu } from '../../data/tourData';
import './Header.css';

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll effect
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close menu when route changes
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location]);

  // Handle body scroll lock when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMenuOpen]);

  // Handle click outside to close menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isMenuOpen && !event.target.closest('.mobile-menu') && !event.target.closest('.hamburger')) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('click', handleClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isMenuOpen]);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const closeMenu = () => {
    setIsMenuOpen(false);
  };

  return (
    <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
      <nav className="navbar">
        <div className="nav-container">
          {/* Logo */}
          <div className="nav-logo">
            <Link to="/" className="logo-link">
              <img 
                src={companyInfo.logo} 
                alt={`${companyInfo.name} Logo`} 
                className="logo-image"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'block';
                }}
              />
              <div className="logo-fallback" style={{ display: 'none' }}>
                <div className="logo-placeholder">
                  <span>NT</span>
                </div>
              </div>
              <h2>{companyInfo.name}</h2>
            </Link>
          </div>

          {/* Desktop Navigation */}
          <ul className="nav-menu">
            {navigationMenu.map((item, index) => (
              <li 
                key={index} 
                className={`nav-item ${item.hasDropdown ? 'dropdown' : ''}`}
              >
                {item.hasDropdown ? (
                  <>
                    <button className="nav-link dropdown-toggle">
                      {item.title} <FaChevronDown className="dropdown-icon" />
                    </button>
                    <ul className="dropdown-menu">
                      {item.submenu.map((subItem, subIndex) => (
                        <li key={subIndex} className="dropdown-item">
                          {subItem.hasSubmenu ? (
                            <div className="dropdown-submenu">
                              <button className="nav-link submenu-toggle">
                                {subItem.title} <FaChevronRight className="submenu-icon" />
                              </button>
                              <ul className="submenu">
                                {subItem.items.map((subSubItem, subSubIndex) => (
                                  <li key={subSubIndex}>
                                    <Link to={subSubItem.path} className="submenu-link">
                                      {subSubItem.name}
                                    </Link>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <Link to={subItem.path || subItem.link} className="dropdown-link">
                              {subItem.name}
                            </Link>
                          )}
                        </li>
                      ))}
                    </ul>
                  </>
                ) : (
                  <Link to={item.path} className="nav-link">
                    {item.title}
                  </Link>
                )}
              </li>
            ))}
          </ul>

          {/* Contact Button */}
          <div className="nav-contact">
            <a 
              href={companyInfo.social.whatsapp} 
              className="contact-btn"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Contact us on WhatsApp"
            >
              <FaWhatsapp />
              <span>WhatsApp</span>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <div className="hamburger" onClick={toggleMenu}>
            <motion.span 
              className="bar"
              animate={isMenuOpen ? { rotate: 45, y: 6 } : { rotate: 0, y: 0 }}
            />
            <motion.span 
              className="bar"
              animate={isMenuOpen ? { opacity: 0 } : { opacity: 1 }}
            />
            <motion.span 
              className="bar"
              animate={isMenuOpen ? { rotate: -45, y: -6 } : { rotate: 0, y: 0 }}
            />
          </div>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMenuOpen && (
            <motion.div 
              className="mobile-menu"
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              transition={{ duration: 0.3 }}
            >
              <div className="mobile-menu-content">
                {/* Close Button */}
                <div className="mobile-menu-header">
                  <button className="mobile-menu-close" onClick={closeMenu}>
                    <span>×</span>
                  </button>
                </div>
                
                {navigationMenu.map((item, index) => (
                  <div key={index} className="mobile-menu-item">
                    {item.hasDropdown ? (
                      <div className="mobile-dropdown">
                        <div className="mobile-dropdown-toggle">
                          {item.title} <FaChevronDown className="dropdown-icon" />
                        </div>
                        <div className="mobile-dropdown-content">
                          {item.submenu.map((subItem, subIndex) => (
                            <div key={subIndex} className="mobile-submenu-item">
                              {subItem.hasSubmenu ? (
                                <div className="mobile-submenu">
                                  <div className="mobile-submenu-toggle">
                                    {subItem.title} <FaChevronRight className="submenu-icon" />
                                  </div>
                                  <div className="mobile-submenu-content">
                                    {subItem.items.map((subSubItem, subSubIndex) => (
                                      <Link 
                                        key={subSubIndex} 
                                        to={subSubItem.path} 
                                        className="mobile-submenu-link"
                                      >
                                        {subSubItem.name}
                                      </Link>
                                    ))}
                                  </div>
                                </div>
                              ) : (
                                <Link to={subItem.path || subItem.link} className="mobile-dropdown-link">
                                  {subItem.name}
                                </Link>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ) : (
                      <Link to={item.path} className="mobile-menu-link">
                        {item.title}
                      </Link>
                    )}
                  </div>
                ))}
                
                {/* Mobile Contact Button */}
                <div className="mobile-contact">
                  <a 
                    href={companyInfo.social.whatsapp} 
                    className="mobile-contact-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <FaWhatsapp />
                    <span>Contact on WhatsApp</span>
                  </a>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </nav>
    </header>
  );
};

export default Header;